import React, { useEffect, useState } from 'react';

import { styled } from '@mui/material/styles';
import { Map, useApiIsLoaded, useMap } from '@vis.gl/react-google-maps';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';

import {
    DEFAULT_LAT,
    DEFAULT_LNG,
    DEFAULT_MAP_PREVIEW_ZOOM,
} from 'common/constants';

const StyledMap = styled('div')`
    align-items: center;
    background-color: #c5c5c5;
    bottom: 0;
    display: flex;
    height: ${({ heightMap }) => (heightMap ? heightMap : '125px')};
    justify-content: center;
    left: 0;
    ${({ disabled }) => disabled && `opacity: .8;`}
    position: relative;
    right: 0;
    top: 0;
`;

const MapContainer = ({
    center = { lat: DEFAULT_LAT, lng: DEFAULT_LNG },
    disableDefaultUI = false,
    handleOnPositionChanged = null,
    heightMap,
    markers = [],
    onMapClicked,
    readOnly,
    zoom = DEFAULT_MAP_PREVIEW_ZOOM,
    mapType = 'roadmap',
}) => {
    const [google, setGoogle] = useState(null);
    const [mapMarkers, setMapMarkers] = useState([]);

    const apiIsLoaded = useApiIsLoaded();
    const map = useMap();

    const onDrag = (e) => {
        if (!google) return;
        const latlng = new google.maps.LatLng(
            e.map.center.lat(),
            e.map.center.lng(),
        );
        mapMarkers.forEach((m) => {
            m.position = latlng;
        });
    };
    const onDragend = (e) => {
        if (handleOnPositionChanged) handleOnPositionChanged(null, e?.map);
    };

    const onZoomChanged = (e) => {
        if (handleOnPositionChanged) handleOnPositionChanged(null, e?.map);
    };

    useEffect(() => {
        if (!apiIsLoaded) return;
        setGoogle(window.google);
    }, [apiIsLoaded]);

    useEffect(() => {
        if (!apiIsLoaded || !google || !map || !center) return;

        const marks = [];
        (markers || []).forEach((m) => {
            const tempMarker = new google.maps.marker.AdvancedMarkerElement({
                position: new google.maps.LatLng(m.lat, m.lng),
                map: map,
            });

            marks.push(tempMarker);
        });

        setMapMarkers(marks);
        map.setCenter({
            lat: parseFloat(center.lat),
            lng: parseFloat(center.lng),
        });

        return () => {
            marks.forEach((marker) => {
                if (marker) {
                    marker.map = null;
                }
            });
        };
    }, [apiIsLoaded, google, map, markers, center]);

    useEffect(() => {
        if (!google || !map || isEmpty(center) || markers === null) return;
        if (center.lat !== null && center.lng !== null) {
            var latlng = new google.maps.LatLng(
                parseFloat(center.lat),
                parseFloat(center.lng),
            );

            mapMarkers.forEach((m) => {
                m.position = latlng;
            });
            map.setCenter(latlng);
        }
    }, [center, markers, google, map, mapMarkers]);

    return (
        <StyledMap disabled={readOnly} heightMap={heightMap}>
            <Map
                disableDefaultUI={disableDefaultUI}
                defaultCenter={{ lat: +center.lat, lng: +center.lng }}
                mapTypeId={mapType}
                mapId={import.meta.env.VITE_GOOGLE_MAPS_DEFAULT_ID}
                onDrag={onDrag}
                onClick={onMapClicked}
                onDragend={onDragend}
                onZoomChanged={onZoomChanged}
                streetViewControl={false}
                defaultZoom={zoom}
            />
        </StyledMap>
    );
};

MapContainer.propTypes = {
    center: PropTypes.object,
    disableDefaultUI: PropTypes.bool,
    handleOnPositionChanged: PropTypes.func,
    heightMap: PropTypes.string,
    markers: PropTypes.array,
    onMapClicked: PropTypes.func,
    readOnly: PropTypes.bool,
    zoom: PropTypes.number,
    mapType: PropTypes.string,
};

export default MapContainer;
